import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { z } from 'zod';

import {
  AttachmentFolder,
  AudioSegment,
  AuditLog,
  InformedIndividual,
  InterviewQuestion,
  ReportComment,
  reportCommentSchema,
  ReportDetail,
  ReportDetailPreview,
  ReportForm,
  ReportFormConfig,
  ReportItem,
  RequestInformationDetails,
  RequestInformationItem,
  ReportNetwork,
  auditLogSchema,
  informedIdividualSchema,
  reportDetailPreviewSchema,
  reportItemSchema,
  ReportSearchResult,
  reportSearchResultSchema,
  ReportActionResponseSchemaProps,
  ReportActionPayloadSchemaProps,
  CaseAction,
  AddCaseActionPayload,
  UpdateCaseActionPayload,
} from './types';

import {
  DefaultMutationResponse,
  PaginatedResponse,
  paginatedParamsSchema,
  parseOrReturnError,
  parsePaginatedResponse,
  restAPI,
} from '../rest-api';

import { RootState } from '~/utils/store';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AtLeastOne<T extends Record<string, any>> = keyof T extends infer K
  ? K extends string
  ? Pick<T, K & keyof T> & Partial<T>
  : never
  : never;

const PRIORITY_VALUES = [
  'Low',
  'Medium',
  'High',
  'Critical',
  'Default',
] as const;

const reportsQueryParams = z
  .object({
    department_name: z.number().optional(),
    // followed_by_user: z.boolean(),
    // only_assigned_reports: z.boolean(),
    ordering: z.string().optional(),
    priority: z.enum(PRIORITY_VALUES).optional(),
    // tag: z.string().nullable(),
  })
  .and(paginatedParamsSchema);

/* eslint-disable @typescript-eslint/no-explicit-any */
const stripUndefined = (obj: {
  [key: string]: any;
}): { [key: string]: any } => {
  const result: { [key: string]: any } = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      result[key] = obj[key];
    }
  }
  return result;
};

const stripNull = (obj: { [key: string]: any }): { [key: string]: any } => {
  const result: { [key: string]: any } = {};
  for (const key in obj) {
    if (obj[key] !== null) {
      result[key] = obj[key];
    }
  }
  return result;
};

const toParams = (
  rawParams: Record<string, any>,
  options?: {
    stripNull?: boolean;
  },
) => {
  const { stripNull: shouldStripNull = true } = options ?? {};
  const paramArg = rawParams;
  let parsed = paramArg ?? {};
  parsed = stripUndefined(paramArg);
  if (shouldStripNull) {
    parsed = stripNull(parsed);
  }

  return new URLSearchParams(parsed);
};

const endpoints = restAPI.injectEndpoints({
  endpoints: (builder) => ({
    reports: builder.query<
      PaginatedResponse<ReportItem>,
      { params?: z.infer<typeof reportsQueryParams> } | void
    >({
      async queryFn(arg, api, _extraOptions, baseQuery) {
        const slug = (api.getState() as RootState).auth.user?.slug;
        const params = toParams(arg && arg.params ? arg.params : {}, {
          stripNull: true,
        });
        const result = await baseQuery({
          url: `/tenant/${slug}/report-list`,
          params: Object.fromEntries(params.entries()),
        });

        if (result.error) {
          return { error: result.error };
        }

        return parseOrReturnError(() =>
          parsePaginatedResponse(reportItemSchema, result.data),
        );
      },
      providesTags: (res) => (res ? [{ type: 'Reports', id: 'list' }] : []),
    }),
    archivedReports: builder.query<
      PaginatedResponse<ReportItem>,
      { slug: string } & {
        params?: Partial<
          z.infer<typeof paginatedParamsSchema> & { channel_id: number }
        >;
      }
    >({
      query({ slug, params }) {
        return {
          url: `/tenant/${slug}/archived-report-list`,
          params: Object.fromEntries(toParams(params ? params : {}).entries()),
        };
      },
      transformResponse(baseQueryReturnValue) {
        return parsePaginatedResponse(reportItemSchema, baseQueryReturnValue);
      },
    }),
    reportPreview: builder.query<ReportDetailPreview, { referenceId: string }>({
      async queryFn(arg, api, _extraOptions, baseQuery) {
        const slug = (api.getState() as RootState).auth.user?.slug;
        const result = await baseQuery({
          url: `/tenant/${slug}/report/${arg.referenceId}?is-pop-up=true`,
        });
        const validated = reportDetailPreviewSchema.safeParse(result.data);
        if (result.error || !validated.success) {
          const error =
            result.error ||
            ({
              status: 'CUSTOM_ERROR',
              error: validated.error?.toString(),
            } as FetchBaseQueryError);
          return { error };
        }

        return { data: validated.data };
      },
      providesTags: (res) =>
        res ? [{ type: 'Reports', id: `${res.id}-preview` }] : [],
    }),
    report: builder.query<
      ReportDetail,
      { tenantSlug: string; referenceId: string }
    >({
      query: ({ referenceId, tenantSlug }) => ({
        url: `/tenant/${tenantSlug}/report/${referenceId}`,
      }),
      providesTags: (res) =>
        res
          ? [
            { type: 'Reports', id: res.id },
            { type: 'Reports', id: 'report-details' },
          ]
          : [],
    }),
    networks: builder.query<
      ReportNetwork,
      {
        referenceId: string;
        params?: Partial<{
          report_type: boolean;
          country: boolean;
          subject_individual: boolean;
          department: boolean;
          start_created_at: string;
          end_created_at: string;
        }>;
      }
    >({
      query({ referenceId, params: _params }) {
        const params = Object.fromEntries(
          toParams(_params ? _params : {}, {
            stripNull: true,
          }).entries(),
        );

        return {
          url: `/related-report-graph/${referenceId}`,
          params,
        };
      },
    }),
    reportForm: builder.query<ReportForm, { slug: string }>({
      query(arg) {
        return {
          url: `/tenant/${arg.slug}/settings/report-form`,
        };
      },
      providesTags: (res) =>
        res ? [{ type: 'Reports', id: 'report-form-config' }] : [],
    }),
    reportFormConfig: builder.query<ReportFormConfig, { slug: string }>({
      query(arg) {
        return {
          url: `/${arg.slug}/report-form`,
        };
      },
    }),
    reportInformedIndividuals: builder.query<
      PaginatedResponse<InformedIndividual>,
      {
        params?: z.infer<typeof paginatedParamsSchema>;
        reportId: string;
      }
    >({
      query(arg) {
        const params = toParams(arg.params ? arg.params : {}, {
          stripNull: true,
        });
        return {
          url: '/informed-individuals',
          params: Object.fromEntries(params.entries()),
        };
      },
      transformResponse(baseQueryReturnValue) {
        return parsePaginatedResponse(
          informedIdividualSchema,
          baseQueryReturnValue,
        );
      },
    }),

    // casemanager attachments
    casemanagerAttachment: builder.query<
      Array<AttachmentFolder>,
      { reportId: string }
    >({
      query: (arg) => ({
        url: `/reports/${arg.reportId}/folders`,
      }),

      providesTags: (res) =>
        res ? [{ type: 'Reports', id: 'casemanager-attachment' }] : [],
    }),

    createFolderCasemanager: builder.mutation<
      void,
      {
        report: string;
        name: string;
        uploaderID: number;
        parent_folder: number | null;
      }
    >({
      query: (args) => ({
        url: '/folders',
        method: 'POST',
        data: args,
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Reports', id: 'casemanager-attachment' }] : [],
    }),

    // delete folder/item
    deleteFolderCasemanager: builder.mutation<
      void,
      {
        folder_id: number;
      }
    >({
      query: (args) => ({
        url: `/folders/${args.folder_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, err) =>
        !err ? [{ type: 'Reports', id: 'casemanager-attachment' }] : [],
    }),

    // rename and move folder
    renameAndMoveFolderCasemanager: builder.mutation<
      void,
      {
        folder_id: number;
        name: string;
        uploaderID?: number;
        report: string;
        parent_folder: number | null | undefined;
      }
    >({
      query: (args) => ({
        url: `/folders/${args.folder_id}`,
        method: 'PATCH',
        data: {
          name: args.name,
          uploaderID: args.uploaderID,
          report: args.report,
          parent_folder: args.parent_folder,
        },
      }),
      invalidatesTags: (_res, err) =>
        !err ? [{ type: 'Reports', id: 'casemanager-attachment' }] : [],
    }),

    // delete folder/item
    deleteFileCasemanager: builder.mutation<
      void,
      {
        parent_folder: number;
        file_id: number;
      }
    >({
      query: (args) => ({
        url: `/folders/${args.parent_folder}/remove-file`,
        method: 'POST',
        data: {
          file_ids: [args.file_id],
        },
      }),
      invalidatesTags: (_res, err) =>
        !err ? [{ type: 'Reports', id: 'casemanager-attachment' }] : [],
    }),

    // delete folder/item
    moveFileCasemanager: builder.mutation<
      void,
      {
        parent_folder: number;
        file_id: number;
        distination_folder: number;
      }
    >({
      query: (args) => ({
        url: `/folders/${args.parent_folder}/move-file`,
        method: 'POST',
        data: {
          file_ids: [args.file_id],
          destination_folder_id: args.distination_folder,
        },
      }),
      invalidatesTags: (_res, err) =>
        !err ? [{ type: 'Reports', id: 'casemanager-attachment' }] : [],
    }),
    rfiList: builder.query<Array<RequestInformationItem>, { reportId: string }>(
      {
        query: ({ reportId }) => ({
          url: `/request-for-information/${reportId}`,
        }),
        providesTags: (res) =>
          res ? [{ type: 'Reports', id: 'request-information-list' }] : [],
      },
    ),
    requestedInformationDetails: builder.query<
      RequestInformationDetails,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/tenant/request-for-information/${id}/details`,
      }),
      providesTags: (res, _err, arg) =>
        res ? [{ type: 'Reports', id: `request-information:${arg.id}` }] : [],
    }),
    createRFI: builder.mutation<
      {
        url: string;
        request_id: number;
      },
      {
        reportId: string;
        recipient_name: string;
        recipient_email: string;
        subject: string;
        due_date: string;
        has_request_to_upload_file: boolean;
        department: number;
      }
    >({
      query: ({ reportId, ...payload }) => ({
        url: `/request-for-information/${reportId}/create-request`,
        method: 'POST',
        data: stripUndefined(payload),
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Reports', id: 'request-information-list' }] : [],
    }),
    updateRFI: builder.mutation<
      DefaultMutationResponse,
      AtLeastOne<{
        email_body: string;
      }> & { rfiId: number }
    >({
      query: ({ rfiId, ...payload }) => ({
        url: `/request-for-information/${rfiId}`,
        method: 'PATCH',
        data: payload,
      }),
      invalidatesTags: (res, _err, arg) =>
        res
          ? [{ type: 'Reports', id: `request-information:${arg.rfiId}` }]
          : [],
    }),
    resendRFIRequest: builder.mutation<
      DefaultMutationResponse,
      { rfiId: number }
    >({
      query: ({ rfiId }) => ({
        url: `/request-for-information/${rfiId}/resend-email`,
        method: 'POST',
      }),
      invalidatesTags: (res, _err, arg) =>
        res
          ? [{ type: 'Reports', id: `request-information:${arg.rfiId}` }]
          : [],
    }),
    closeRFI: builder.mutation<void, { rfiID: number; closure_reason: string }>(
      {
        query: ({ rfiID, ...payload }) => ({
          url: `/tenant/request-for-information/${rfiID}/close-request`,
          method: 'PATCH',
          data: payload,
        }),
        invalidatesTags: (res, _err, arg) =>
          res
            ? [
              { type: 'Reports', id: `request-information:${arg.rfiID}` },
              { type: 'Reports', id: 'request-information-list' },
            ]
            : [],
      },
    ),
    //    descryptReport: builder.query<ReportDetail, { referenceId: string }>({
    // queryFn(arg, api, extraOptions, baseQuery) {

    // },
    //    })
    /**
     * Audit logs
     */
    reportAuditLogs: builder.query<
      PaginatedResponse<AuditLog>,
      { referenceId: string; params?: z.infer<typeof paginatedParamsSchema> }
    >({
      query: ({ referenceId, params: _params }) => {
        const params = Object.fromEntries(
          toParams(_params ? _params : {}, {
            stripNull: true,
          }).entries(),
        );
        return {
          url: `/report-audit-logs/${referenceId}`,
          params,
        };
      },
      transformResponse(baseQueryReturnValue) {
        return parsePaginatedResponse(auditLogSchema, baseQueryReturnValue);
      },
      providesTags: [{ type: 'Reports', id: 'audit-logs' }],
    }),
    /**
     * Interview Questions
     */
    interviewQuestions: builder.query<
      Array<InterviewQuestion>,
      { referenceId: string }
    >({
      query: ({ referenceId }) => ({
        url: `/report/${referenceId}/interview-questions`,
      }),
      providesTags: (res, _err, arg) =>
        res
          ? [
            { type: 'Reports', id: arg.referenceId },
            { type: 'Reports', id: 'interview-questions' },
          ]
          : [],
    }),
    createInterviewQuestion: builder.mutation<
      Array<InterviewQuestion>,
      { referenceId: string; questions: Array<{ question: string }> }
    >({
      query: ({ referenceId, questions }) => ({
        url: `/report/${referenceId}/interview-questions`,
        method: 'POST',
        data: questions,
      }),
      invalidatesTags: (res, _err, arg) =>
        res
          ? [
            { type: 'Reports', id: arg.referenceId },
            { type: 'Reports', id: 'interview-questions' },
          ]
          : [],
    }),
    updateQuestionOrder: builder.mutation<
      void,
      { referenceId: string; questions: Array<{ id: number; order: number }> }
    >({
      query: ({ referenceId, questions }) => ({
        url: `/report/${referenceId}/interview-questions/update-order`,
        method: 'PATCH',
        data: questions,
      }),
      invalidatesTags: (res, _err, arg) =>
        res
          ? [
            { type: 'Reports', id: arg.referenceId },
            { type: 'Reports', id: 'interview-questions' },
          ]
          : [],
    }),
    deleteInterviewQuestion: builder.mutation<
      void,
      { referenceId: string; questionId: number }
    >({
      query: ({ referenceId, questionId }) => ({
        url: `/report/${referenceId}/interview-questions/${questionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, err, arg) =>
        !err
          ? [
            { type: 'Reports', id: arg.referenceId },
            { type: 'Reports', id: 'interview-questions' },
          ]
          : [],
    }),
    reportComments: builder.query<
      PaginatedResponse<ReportComment>,
      {
        referenceId: string;
        search: string;
        mentionsOnly: string;
        params?: z.infer<typeof paginatedParamsSchema>;
      }
    >({
      query: ({ referenceId, search, mentionsOnly, params: _params }) => {
        const params = Object.fromEntries(
          toParams(
            _params
              ? {
                ..._params,
                search,
                mentions_only: mentionsOnly,
              }
              : {},
            {
              stripNull: true,
            },
          ).entries(),
        );
        return {
          url: `/report/${referenceId}/report-comments`,
          params,
        };
      },
      transformResponse(baseQueryReturnValue) {
        return parsePaginatedResponse(
          reportCommentSchema,
          baseQueryReturnValue,
        );
      },
      providesTags: (res, _err, arg) =>
        res
          ? [
            { type: 'Reports', id: arg.referenceId },
            { type: 'Reports', id: 'report-comments' },
          ]
          : [],
    }),
    createComment: builder.mutation<
      PaginatedResponse<ReportComment>,
      {
        referenceId: string;
        comment: {
          content: string;
          tagged_users: Array<number>;
          parent?: number;
        };
      }
    >({
      query: ({ referenceId, comment }) => ({
        url: `/report/${referenceId}/report-comments`,
        method: 'POST',
        data: comment,
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Reports', id: 'report-comments' }] : [],
    }),
    markCommentRead: builder.mutation<
      PaginatedResponse<ReportComment>,
      {
        referenceId: string;
        commentId: number;
      }
    >({
      query: ({ referenceId, commentId }) => ({
        url: `/report/${referenceId}/report-comments/${commentId}/seen`,
        method: 'POST',
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Reports', id: 'report-comments' }] : [],
    }),
    markCommentResolve: builder.mutation<
      PaginatedResponse<ReportComment>,
      {
        referenceId: string;
        commentId: number;
        resolved: boolean;
      }
    >({
      query: ({ referenceId, commentId, resolved }) => ({
        url: `/report/${referenceId}/report-comments/${commentId}/resolved`,
        method: 'POST',
        data: { resolved },
      }),
      invalidatesTags: (res) =>
        res ? [{ type: 'Reports', id: 'report-comments' }] : [],
    }),
    deleteComment: builder.mutation<
      PaginatedResponse<ReportComment>,
      {
        referenceId: string;
        commentId: number;
      }
    >({
      query: ({ referenceId, commentId }) => ({
        url: `/report/${referenceId}/report-comments/${commentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Reports', id: 'report-comments' }],
    }),
    recordingTranscription: builder.query<
      {
        TranscriptionJob?: {
          TranscriptionJobStatus?: string;
          Transcript?: {
            TranscriptFileUri?: string;
          };
        };
      },
      { fileId: number }
    >({
      query: ({ fileId }) => ({
        url: `/report-files/${fileId}/transcription-details`,
      }),
      providesTags: (res, _err, arg) =>
        res
          ? [
            {
              type: 'Reports',
              id: `report-recording-transcription:${arg.fileId}`,
            },
          ]
          : [],
    }),
    downloadTranscription: builder.query<
      {
        results: {
          transcripts: Array<{ transcript: string }>;
          audio_segments: Array<AudioSegment>;
        };
      },
      { transcriptionURL: string }
    >({
      query: ({ transcriptionURL }) => ({
        url: '',
        baseURLOverride: transcriptionURL,
        responseType: 'blob',
        headers: {
          Accept: 'application/octet-stream',
        },
      }),
      extraOptions: { requireAuth: false },
    }),
    reportSearch: builder.query<
      Array<ReportSearchResult>,
      {
        payload: { search_text: string } & Partial<{
          comments__category: Array<number>;
          report_type: Array<number>;
          priority: Array<number>;
          final_due_date: Array<string>;
        }>;
        params: { form_id: number };
      }
    >({
      query: (args) => {
        return {
          method: 'POST',
          url: '/search/search-reports',
          data: stripUndefined(args.payload),
          params: stripUndefined(args.params),
        };
      },
      // we manually parse this since we want to modify the shape of this response
      transformResponse: (res) => reportSearchResultSchema.array().parse(res),
      providesTags: (res) =>
        res ? [{ type: 'Reports', id: 'report-advanced-search' }] : [],
    }),
    reportActionUpdate: builder.mutation<ReportActionResponseSchemaProps,
      {
        report_reference: string;
        slug: string;
        payload: AtLeastOne<ReportActionPayloadSchemaProps>;
      }
    >({
      query: (args) => ({
        url: `/tenant/${args.slug}/report/update/${args.report_reference}`,
        method: 'PATCH',
        data: args.payload,
      }),
      invalidatesTags: (res) =>
        //no request to invalidate yet
        res ? [] : [],
    }),
    //CASE ACTIONS
    getCaseActions: builder.query<Array<CaseAction>, {
      slug: string;
      channelId: number;
    }>({
      query: ({ slug, channelId }) => ({
        url: `/intake-channel/tenant/${slug}/${channelId}/manager-fields`,
      }),
      providesTags: [{ type: 'Reports', id: 'case-actions-settings' }],
    }),
    addCaseAction: builder.mutation<CaseAction, {
      slug: string;
      channelId: number;
      payload: AddCaseActionPayload
    }>({
      query: ({ slug, channelId, payload }) => ({
        url: `/intake-channel/tenant/${slug}/${channelId}/manager-fields`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (res) => res ? [{ type: 'Reports', id: 'case-actions-settings' }] : [],
    }),
    updateCaseAction: builder.mutation<CaseAction, { slug: string, channelId: number, payload: UpdateCaseActionPayload[] }>({
      query: ({ slug, channelId, payload }) => ({
        url: `/intake-channel/tenant/${slug}/${channelId}/manager-fields/update-fields`,
        method: 'PATCH',
        data: payload,
      }),
      invalidatesTags: (res) => res ? [{ type: 'Reports', id: 'case-actions-settings' }] : [],
    }),
    updateCaseActionOrder: builder.mutation<void, { slug: string, channelId: number, payload: Array<{ id: number, order?: number, is_display_on_report_actions_enabled: boolean }> }>({
      query: ({ slug, channelId, payload }) => ({
        url: `/intake-channel/tenant/${slug}/${channelId}/manager-fields/update-fields`,
        method: 'PATCH',
        data: payload,
      }),
      invalidatesTags: (res) => res ? [{ type: 'Reports', id: 'case-actions-settings' }] : [],
    }),
    deleteCaseAction: builder.mutation<void, { slug: string, channelId: number, id: number }>({
      query: ({ slug, channelId, id }) => ({
        url: `/intake-channel/tenant/${slug}/${channelId}/manager-fields/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Reports', id: 'case-actions-settings' }],
    }),
  }),
  overrideExisting: false,
});

export const ReportService = endpoints;
